import React from 'react'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { hexColors } from '@mobi/settings/hexColors'
import { toTitleCase } from '@mobi/utils/string'
import { hasTips } from '@core/Areas/Racing/Components/BettableTips'
import {
  BettableTips,
  type Props as BettableTipsProps,
} from '@core/Areas/Racing/Components/BettableTips/BettableTips'
import { RacePreviewTipsCard } from '@core/Areas/Racing/Components/RaceContent/RacePreviewTipsCard'
import { useRaceSportsContentCardProps } from '@core/Areas/Racing/Hooks/RaceSportsContent'
import { useTipsterDisplay } from '@core/Areas/Racing/Hooks/useTipsterDisplay'
import type { IdentifiableRace, RaceStatus } from '@core/Areas/Racing/Types'
import { ExpandablePanelThemes } from '@core/Components/ExpandablePanel/themes'
import { CompactPanel } from '@core/Components/Panel'
import { Text } from '@core/Components/Text'
import { useFeature } from '@core/Utils/hooks'
import { useRaceContentCardProps } from '../../Hooks/RaceContent/useRaceContentCardProps'
import { PreviewTipsEvent } from '../RaceContent/helpers/analytics'
import { TipsPanelCarousel } from './TipsPanelCarousel'

type NewProps = BettableTipsProps &
  IdentifiableRace & {
    tipster: string
    racecourseSeq?: number
  }

type Props = NewProps | OldProps
export const BettableTipsPanel = (props: Props): JSX.Element => {
  const isNewPreviewTipsLayoutActive = useFeature('new-preview-tips-layout')

  if (isNewPreviewTipsLayoutActive) {
    return <BettableTipsPanelNew {...(props as NewProps)} />
  }
  return <BettableTipsPanelOld {...(props as OldProps)} />
}

const BettableTipsPanelNew = ({
  tipster,
  tips,
  isRaceOpen,
  raceKey,
  race,
  meetingId,
  racecourseSeq,
  meetingDate,
  raceNumber,
}: NewProps): JSX.Element => {
  const hasValidTips = hasTips(tips.toString(), tipster)
  const tipsterDisplay = useTipsterDisplay(toTitleCase(tipster))
  const raceCardProps = useRaceContentCardProps(meetingId, meetingDate, raceNumber)
  const sportsCardProps = useRaceSportsContentCardProps(meetingDate, raceNumber, racecourseSeq)

  const basePreviewTipsEventData = {
    raceCode: race?.MeetingInformation?.MeetingCode || '',
    meetingName: race?.MeetingInformation?.MeetingName || '',
    raceStatus: race?.MeetingInformation?.SelectedRace?.RaceStatus as RaceStatus,
  }

  // analytics
  const interactionType: PreviewTipsEvent['interaction_type'] = 'View Content'
  const previewTipsEventData: PreviewTipsEvent[] = [
    ...(raceCardProps
      ? [
          {
            interaction_type: interactionType,
            author: raceCardProps.authorTitle,
            ...basePreviewTipsEventData,
          },
        ]
      : []),
    ...(sportsCardProps
      ? [
          {
            interaction_type: interactionType,
            author: 'Racing & Sports',
            ...basePreviewTipsEventData,
          },
        ]
      : []),
    ...(hasValidTips
      ? [
          {
            interaction_type: interactionType,
            author: `${toTitleCase(tipster)} (Radio TAB)`,
            ...basePreviewTipsEventData,
          },
        ]
      : []),
  ]

  return (
    <TipsPanelCarousel previewTipsEventData={previewTipsEventData}>
      {raceCardProps && (
        <RacePreviewTipsCard
          {...raceCardProps}
          raceKey={raceKey}
          isRaceOpen={isRaceOpen}
          race={race}
          tipSource='tips-previews'
          author={raceCardProps.authorTitle}
        />
      )}
      {sportsCardProps && (
        <RacePreviewTipsCard
          {...sportsCardProps}
          raceKey={raceKey}
          isRaceOpen={isRaceOpen}
          race={race}
          tipSource='tips-previews'
          author='Racing & Sports'
        />
      )}
      {hasValidTips && (
        <RacePreviewTipsCard
          title={<span data-testid='bettable-tips-header-text'>{tipsterDisplay} Tips</span>}
          headerTrailContent={<Icon name='SolidStar01' size='1.8rem' color={hexColors.white} />}
          tips={tips}
          race={race}
          raceKey={raceKey}
          isRaceOpen={isRaceOpen}
          tipSource='tips-previews'
          author={`${toTitleCase(tipster)} (Radio TAB)`}
        />
      )}
    </TipsPanelCarousel>
  )
}

type OldProps = BettableTipsProps & {
  tipster: string
}

const BettableTipsPanelOld = ({
  tipster,
  tips,
  isRaceOpen,
  raceKey,
  race,
}: OldProps): JSX.Element => {
  const tipsterDisplay = useTipsterDisplay(tipster)

  return (
    <CompactPanel
      themeName={ExpandablePanelThemes.lavender}
      headerText={<Text data-testid='bettable-tips-header-text'>{tipsterDisplay} Tips</Text>}
      headerIcon={<Icon name='SolidStar01' size='1.8rem' color={hexColors.blueDiamond} />}
    >
      <BettableTips
        tips={tips}
        race={race}
        raceKey={raceKey}
        isRaceOpen={isRaceOpen}
        tipSource='tips'
      />
    </CompactPanel>
  )
}

export const getRaceStatus = (
  status?: string
): 'open' | 'closed' | 'abandoned' | 'interim' | 'released' | undefined => {
  switch (status) {
    case 'open':
    case 'closed':
    case 'abandoned':
    case 'interim':
    case 'released':
      return status
    default:
      return undefined
  }
}
